/**
 * LocalStorage Adapter
 * Base44 API'sini taklit eden localStorage tabanlı veri katmanı
 * Offline/demo mod için idealdir
 */

const STORAGE_PREFIX = 'base44_';

/**
 * Benzersiz ID oluşturur
 */
function generateId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Entity verilerini localStorage'dan okur
 */
function getEntityData(entityName) {
  const key = `${STORAGE_PREFIX}${entityName}`;
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
}

/**
 * Entity verilerini localStorage'a yazar
 */
function setEntityData(entityName, data) {
  const key = `${STORAGE_PREFIX}${entityName}`;
  localStorage.setItem(key, JSON.stringify(data));
}

/**
 * Entity sınıfı - CRUD operasyonları
 */
class Entity {
  constructor(name) {
    this.name = name;
  }

  /**
   * Tüm kayıtları listeler
   * @param {string} sortField - Sıralama alanı (opsiyonel, - prefix = desc)
   */
  async list(sortField = null) {
    const data = getEntityData(this.name);

    if (sortField) {
      const desc = sortField.startsWith('-');
      const field = desc ? sortField.slice(1) : sortField;

      data.sort((a, b) => {
        const aVal = a[field] || '';
        const bVal = b[field] || '';

        if (typeof aVal === 'string') {
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        return desc ? bVal - aVal : aVal - bVal;
      });
    }

    return data;
  }

  /**
   * Filtrelenmiş kayıtları döndürür
   * @param {Object} filters - Filtre kriterleri
   * @param {string} sortField - Sıralama alanı
   */
  async filter(filters = {}, sortField = null) {
    let data = getEntityData(this.name);

    // Filtreleme
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        data = data.filter(item => {
          const itemValue = item[key];

          // Array içinde arama
          if (Array.isArray(value)) {
            return value.includes(itemValue);
          }

          // String eşleşmesi (case-insensitive)
          if (typeof itemValue === 'string' && typeof value === 'string') {
            return itemValue.toLowerCase().includes(value.toLowerCase());
          }

          // Tam eşleşme
          return itemValue === value;
        });
      }
    });

    // Sıralama
    if (sortField) {
      const desc = sortField.startsWith('-');
      const field = desc ? sortField.slice(1) : sortField;

      data.sort((a, b) => {
        const aVal = a[field] || '';
        const bVal = b[field] || '';

        if (typeof aVal === 'string') {
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        return desc ? bVal - aVal : aVal - bVal;
      });
    }

    return data;
  }

  /**
   * Tek kayıt getirir
   * @param {string} id - Kayıt ID
   */
  async get(id) {
    const data = getEntityData(this.name);
    const item = data.find(d => d.id === id);

    if (!item) {
      throw new Error(`${this.name} with id ${id} not found`);
    }

    return item;
  }

  /**
   * Yeni kayıt oluşturur
   * @param {Object} record - Kayıt verileri
   */
  async create(record) {
    const data = getEntityData(this.name);

    const newRecord = {
      ...record,
      id: generateId(),
      created_date: new Date().toISOString(),
    };

    data.push(newRecord);
    setEntityData(this.name, data);

    return newRecord;
  }

  /**
   * Kayıt günceller
   * @param {string} id - Kayıt ID
   * @param {Object} updates - Güncellenecek alanlar
   */
  async update(id, updates) {
    const data = getEntityData(this.name);
    const index = data.findIndex(d => d.id === id);

    if (index === -1) {
      throw new Error(`${this.name} with id ${id} not found`);
    }

    data[index] = {
      ...data[index],
      ...updates,
      updated_date: new Date().toISOString(),
    };

    setEntityData(this.name, data);

    return data[index];
  }

  /**
   * Kayıt siler
   * @param {string} id - Kayıt ID
   */
  async delete(id) {
    const data = getEntityData(this.name);
    const index = data.findIndex(d => d.id === id);

    if (index === -1) {
      throw new Error(`${this.name} with id ${id} not found`);
    }

    data.splice(index, 1);
    setEntityData(this.name, data);

    return { success: true };
  }

  /**
   * Toplu kayıt oluşturur
   * @param {Array} records - Kayıt listesi
   */
  async createMany(records) {
    const data = getEntityData(this.name);
    const newRecords = records.map(record => ({
      ...record,
      id: generateId(),
      created_date: new Date().toISOString(),
    }));

    data.push(...newRecords);
    setEntityData(this.name, data);

    return newRecords;
  }

  /**
   * Tüm kayıtları siler
   */
  async deleteAll() {
    setEntityData(this.name, []);
    return { success: true };
  }

  /**
   * Kayıt sayısını döndürür
   */
  async count(filters = {}) {
    const data = await this.filter(filters);
    return data.length;
  }
}

/**
 * LocalStorage Adapter - Entity'leri oluşturur
 */
export const LocalStorageAdapter = {
  entities: {
    Lead: new Entity('Lead'),
    Task: new Entity('Task'),
    Product: new Entity('Product'),
    Order: new Entity('Order'),
    CustomerPrice: new Entity('CustomerPrice'),
    ActivityReport: new Entity('ActivityReport'),
    IncomingOffer: new Entity('IncomingOffer'),
    Agent: new Entity('Agent'),
    User: new Entity('Agent'),
    PdfSetting: new Entity('PdfSetting'),
    Notification: new Entity('Notification'),
    Supplier: new Entity('Supplier'),
  },

  /**
   * Tüm verileri temizler
   */
  clearAll() {
    Object.keys(localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  },

  /**
   * Verileri export eder
   */
  exportData() {
    const data = {};
    Object.keys(localStorage)
      .filter(key => key.startsWith(STORAGE_PREFIX))
      .forEach(key => {
        const entityName = key.replace(STORAGE_PREFIX, '');
        data[entityName] = JSON.parse(localStorage.getItem(key));
      });
    return data;
  },

  /**
   * Verileri import eder
   */
  importData(data) {
    Object.entries(data).forEach(([entityName, records]) => {
      setEntityData(entityName, records);
    });
  },
};

export default LocalStorageAdapter;
